/* nplus-cart.js — bundle cart service (frontend phase).
   Classic script: exposes window.nplusCart. No dependencies.
   Pure helpers are assertion-checkable in the browser console (see plan Task 2). */
(function () {
  'use strict';

  var BUNDLE = '_nplus_bundle';
  var ROLE = '_nplus_role';

  function makeBundleId() {
    if (self.crypto && typeof self.crypto.randomUUID === 'function') {
      return self.crypto.randomUUID();
    }
    return 'b-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function propsFor(bundleId, role) {
    var p = {};
    p[BUNDLE] = bundleId;
    p[ROLE] = role;
    return p;
  }

  function prop(item, name) {
    return item && item.properties ? item.properties[name] : undefined;
  }

  // Build the /cart/add.js items array for a bike + optional gift + optional accessories.
  // opts: { bikeVariantId, gift?: { included, variantId }, accessories?: [{ variantId }], bundleId? }
  function buildBundleItems(opts) {
    var items = [{
      id: Number(opts.bikeVariantId),
      quantity: 1
    }];
    if (opts.gift && opts.gift.included && opts.gift.variantId) {
      items.push({
        id: Number(opts.gift.variantId),
        quantity: 1
      });
    }
    (opts.accessories || []).forEach(function (a) {
      if (a && a.variantId) {
        // Free gift accessories ride the 'gift' role so the existing reconcile
        // (orphan-remove + qty-clamp) and the future Discount Function cover them.
        items.push({ id: Number(a.variantId), quantity: 1 });
      }
    });
    return items;
  }

  // Gift line keys whose primary (bike) sibling is no longer in the cart.
  function findOrphanGiftKeys(items) {
    items = items || [];
    var primaries = {};
    items.forEach(function (it) {
      if (prop(it, ROLE) === 'primary') primaries[prop(it, BUNDLE)] = true;
    });
    var orphans = [];
    items.forEach(function (it) {
      if (prop(it, ROLE) === 'gift' && !primaries[prop(it, BUNDLE)]) {
        orphans.push(it.key);
      }
    });
    return orphans;
  }

  // Gift line keys for non-orphan bundles whose quantity exceeds 1 (R6 clamp).
  function findOverQtyGiftKeys(items) {
    items = items || [];
    var primaries = {};
    items.forEach(function (it) {
      if (prop(it, ROLE) === 'primary') primaries[prop(it, BUNDLE)] = true;
    });
    return items
      .filter(function (it) {
        return prop(it, ROLE) === 'gift' && primaries[prop(it, BUNDLE)] && it.quantity > 1;
      })
      .map(function (it) { return it.key; });
  }

  // ---- Ajax helpers ----
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/change failed: ' + r.status);
      return r.json();
    });
  }

  // Returns the theme's reactive cart store if it can add items, else null.
  function themeCartStore() {
    try {
      var s = window.Alpine && window.Alpine.store && window.Alpine.store('cart');
      return (s && typeof s.addItem === 'function') ? s : null;
    } catch (e) { return null; }
  }

  // Add bike + optional gift in one request.
  // Preferred path: the theme's store.addItem — it adds the items, re-renders the
  // cart sections via the Section Rendering API (so the mini-cart drawer shows the
  // new lines), opens the drawer, refreshes store.data, and surfaces cart errors
  // through the theme's own notification UI. A raw /cart/add.js (fallback) updates
  // counts but leaves the drawer HTML stale, so it's used only when the store is absent.
  // Returns a promise; the fallback rejects on failure so the caller shows an inline error.
  function addBundle(opts) {
    var items = buildBundleItems(opts);
    var store = themeCartStore();
    if (store) {
      return Promise.resolve(store.addItem({ items: items }));
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw new Error(e && e.description || 'add failed'); });
      return r.json();
    }).then(function (cart) {
      refreshThemeCart();
      openMiniCart();
      return cart;
    });
  }

  function refreshThemeCart() {
    try {
      var store = window.Alpine && window.Alpine.store && window.Alpine.store('cart');
      if (store && typeof store.fetchCart === 'function') store.fetchCart();
    } catch (e) { /* theme store unavailable; cart still updated server-side */ }
  }

  function openMiniCart() {
    try {
      var drawers = window.Alpine && window.Alpine.store && window.Alpine.store('drawers');
      if (drawers && typeof drawers.open === 'function') drawers.open('mini_cart');
    } catch (e) { /* no drawer to open */ }
  }

  // ---- Integrity (R5 + R6), data-level ----
  var reconciling = false;
  var reconcilePending = false;

  function reconcile(items) {
    if (reconciling) { reconcilePending = true; return Promise.resolve(); }
    var orphans = findOrphanGiftKeys(items);
    var overQty = findOverQtyGiftKeys(items);
    if (!orphans.length && !overQty.length) return Promise.resolve();

    reconciling = true;
    var store = themeCartStore();
    // [key, targetQuantity] — orphan gifts removed (0), over-qty gifts clamped (1).
    var ops = orphans.map(function (k) { return [k, 0]; })
      .concat(overQty.map(function (k) { return [k, 1]; }));

    // Route through the theme's changeItem (qty 0 removes, 1 clamps) so the
    // section-rendered mini-cart drawer reflects the correction; fall back to a
    // raw /cart/change.js when the theme store is absent.
    var applyOp = (store && typeof store.changeItem === 'function')
      ? function (op) { return store.changeItem(op[0], op[1]); }
      : function (op) { return changeLine(op[0], op[1]); };

    // Sequential changes; line keys stay valid across changes.
    var chain = ops.reduce(function (p, op) {
      return p.then(function () { return applyOp(op); });
    }, Promise.resolve());

    return chain
      .then(function () { if (!store) refreshThemeCart(); })
      .catch(function (err) { console.warn('[nplus] reconcile failed', err); })
      .then(function () {
        reconciling = false;
        if (reconcilePending) {
          reconcilePending = false;
          refreshThemeCart(); // re-fetch → re-fires the Alpine effect to reconcile the latest cart
        }
      });
  }

  // ---- init: hook the theme's reactive Alpine cart store ----
  var started = false;
  function init() {
    if (started) return;
    started = true;
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      var store = window.Alpine && window.Alpine.store && window.Alpine.store('cart');
      if (store) {
        clearInterval(timer);
        try {
          // store object identity is stable in Alpine; reading store.data.items inside the effect tracks it reactively
          window.Alpine.effect(function () {
            var items = store.data && store.data.items;
            if (items) reconcile(items);
          });
        } catch (e) {
          console.warn('[nplus] could not attach cart reconcile effect', e);
        }
      } else if (attempts > 50) { // ~10s
        clearInterval(timer);
        console.warn('[nplus] Alpine cart store unavailable; reconcile disabled (Function backstops at cutover)');
      }
    }, 200);
  }

  window.nplusCart = {
    _BUNDLE: BUNDLE,
    _ROLE: ROLE,
    makeBundleId: makeBundleId,
    buildBundleItems: buildBundleItems,
    findOrphanGiftKeys: findOrphanGiftKeys,
    findOverQtyGiftKeys: findOverQtyGiftKeys,
    getCart: getCart,
    changeLine: changeLine,
    addBundle: addBundle,
    reconcile: reconcile,
    init: init
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
